import { useState } from 'react'

import { api } from '@/lib/api'

/**
 * Écran de connexion.
 *
 * Un seul champ : le mot de passe unique du `.env`. Il n'y a ni identifiant,
 * ni inscription, ni récupération — l'application est personnelle, et chaque
 * mécanisme supplémentaire serait une surface d'attaque de plus.
 *
 * Le mot de passe n'est jamais conservé côté navigateur : le serveur répond
 * avec un cookie `HttpOnly`, hors de portée de tout script de la page.
 */
export function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!username || !password || busy) return

    setBusy(true)
    setError(null)
    try {
      await api.login(username, password)
      setPassword('')
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connexion impossible')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="safe-top safe-bottom safe-x flex min-h-full items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm">
        <h1 className="mb-1 text-center text-[20px] font-semibold tracking-tight text-ink-strong">
          Reverse<span className="text-accent">Trader</span>
        </h1>
        <p className="mb-6 text-center text-[12px] text-ink-muted">
          Application privée — accès par identifiant
        </p>

        <label className="mb-3 block">
          <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-muted">
            Identifiant
          </span>
          <input
            type="text"
            value={username}
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="username"
            onChange={(event) => setUsername(event.target.value)}
            className="min-h-touch w-full rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-muted">
            Mot de passe
          </span>
          <input
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
            className="min-h-touch w-full rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
          />
        </label>

        {error && (
          <p className="mt-3 rounded-md border border-down/40 bg-down/10 p-2 text-[12px] text-down">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !username || !password}
          className="mt-4 min-h-touch w-full rounded-md bg-accent text-[14px] font-semibold text-white disabled:opacity-40"
        >
          {busy ? 'Connexion…' : 'Se connecter'}
        </button>

        <p className="mt-6 text-center text-[11px] leading-relaxed text-ink-muted">
          Le mot de passe est celui défini dans <code className="num">APP_PASSWORD</code> du
          fichier <code className="num">.env</code>.
        </p>
      </form>
    </div>
  )
}
